// Added by Sejune Oh on 2019-04-01
// Copyright (c) Coreline Soft, All rights are reserved.
//////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
// Store
import StoreBase, { StoreBaseConstructorProps } from './StoreBase'

//////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
// Define
import { GetQueryReturn, API_VERSION } from '../../helper/define'

//////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
// Interface
interface MgmtStoreBaseConstructorProps extends StoreBaseConstructorProps {
    apiUrl: string
}

//////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
export default class MgmtStoreBase<T> extends StoreBase<T> {
    _apiUrl: string = ''

    constructor({ storeName, apiUrl, _root }: MgmtStoreBaseConstructorProps) {
        super({ storeName, _root })
        this._apiUrl = `${API_VERSION}/${apiUrl}`
    }

    setApiUrl(apiUrl: string) {
        this._apiUrl = `${API_VERSION}/${apiUrl}`
    }

    getSingle(id: string): Promise<T> {
        return new Promise(async (resolve, reject) => {
            try {
                const { data: rtnData } = await super.get({ url: `${this._apiUrl}/${id}` })
                resolve(rtnData)
            } catch (err) {
                reject(err)
            }
        })
    }

    getSingleWithoutId(subPath?: string): Promise<T> {
        return new Promise(async (resolve, reject) => {
            try {
                const { data: rtnData } = await super.get({ url: `${this._apiUrl}${subPath || ''}` })
                resolve(rtnData)
            } catch (err) {
                reject(err)
            }
        })
    }

    getAll(getQuery: string, subPath = ''): Promise<GetQueryReturn<T>> {
        return new Promise(async (resolve, reject) => {
            try {
                const { data: rtnData } = await super.get({ url: `${this._apiUrl}${subPath}?${getQuery}` })
                resolve(rtnData)
            } catch (err) {
                reject(err)
            }
        })
    }

    getAssigned<T>(id: string, property: string): Promise<GetQueryReturn<T>> {
        return new Promise(async (resolve, reject) => {
            try {
                const { data: rtnData } = await super.get({ url: `${this._apiUrl}/${id}/assigned-${property}` })
                resolve(rtnData)
            } catch (err) {
                reject(err)
            }
        })
    }

    add(element: T): Promise<T> {
        return new Promise(async (resolve, reject) => {
            try {
                const { data: rtnData } = await super.post({ url: `${this._apiUrl}`, data: element })
                resolve(rtnData)
            } catch (err) {
                reject(err)
            }
        })
    }

    postCustom({ subUrl, element }: { element?: T; subUrl?: string }): Promise<T> {
        return new Promise(async (resolve, reject) => {
            try {
                const { data: rtnElement } = await super.post({ url: `${this._apiUrl}${subUrl}`, data: element })
                resolve(rtnElement)
            } catch (err) {
                reject(err)
            }
        })
    }

    remove(id: string): Promise<{}> {
        return new Promise(async (resolve, reject) => {
            try {
                await super.delete({ url: `${this._apiUrl}/${id}` })
                resolve(true)
            } catch (err) {
                reject(err)
            }
        })
    }

    updateSingle(id: string, element: T): Promise<T> {
        return new Promise(async (resolve, reject) => {
            try {
                const { data: rtnData } = await super.put({
                    url: `${this._apiUrl}/${id}`,
                    data: {
                        id,
                        ...element,
                    },
                })
                resolve(rtnData)
            } catch (err) {
                reject(err)
            }
        })
    }

    updateAssigned(id: string, property: string, assignedKey: string, assignedElementsKey: Array<string>): Promise<T> {
        return new Promise(async (resolve, reject) => {
            try {
                const { data: rtnData } = await super.put({
                    url: `${this._apiUrl}/${id}/assigned-${property}`,
                    data: {
                        id,
                        [`${assignedKey}`]: assignedElementsKey,
                    },
                })
                resolve(rtnData)
            } catch (err) {
                reject(err)
            }
        })
    }

    putCustom({ id, subPath, element }: { id?: string; subPath?: string; element?: any }): Promise<T> {
        return new Promise(async (resolve, reject) => {
            try {
                const apiPath = `${this._apiUrl}` + (id ? `/${id}` : '') + (subPath ? `/${subPath}` : '')
                const { data: rtnData } = await super.put({
                    url: apiPath,
                    data: {
                        ...element,
                    },
                })

                resolve(rtnData)
            } catch (err) {
                reject(err)
            }
        })
    }

    async checkDuplicatedByField({ value, field, id }: { value: string; field: string; id?: string }) {
        const filter = { $op: 'and', [`${field}`]: `${value}` }
        const path = this.getQueryPath({ pageIndex: 0, orderBy: `${field}`, filter: JSON.stringify(filter) })
        const rtnData = await this.getAll(path)
        if (id) {
            const exceptItSelf = (rtnData.items as any).filter((item: { id: string}) => item.id !== id)
            return exceptItSelf.length > 0
        }
        return (rtnData.items as Array<T>).length > 0
    }

    async checkDuplicatedByMultiField({ values, fields }: { values: Array<string>; fields: Array<string> }) {
        let filter = { $op: 'and' }
        fields.forEach((field, index) => {
            // @ts-ignore
            filter[`${field}`] = `${values[index]}`
        })

        const path = this.getQueryPath({ pageIndex: 0, filter: JSON.stringify(filter) })
        const rtnData = await this.getAll(path)
        return (rtnData.items as Array<T>).length > 0
    }
}
